import { createSlice } from '@reduxjs/toolkit';
import { formatNameLastFirst, sortAsc } from '@strata/helper-functions';

import {
    getDiagnoses,
    getMedications,
    getParentSuppliers,
    getPayers,
    getPlacesOfService,
    getPrescriberInfo,
    getSupplierInfo,
} from 'api/referenceAPI';
import { handleError } from 'services/Error';

export const referenceSlice = createSlice({
    name: 'reference',
    initialState: {
        diagnosisList: undefined,
        payerList: undefined,
        medicationList: undefined,
        prescriberList: undefined,
        supplierList: undefined,
        supplierDetails: {},
        productConfigList: {},
        placeOfServiceList: undefined,
    },
    reducers: {
        setDiagnosisList(state, action) {
            return { ...state, diagnosisList: action.payload };
        },
        setPayerList(state, action) {
            return { ...state, payerList: action.payload };
        },
        setMedicationList(state, action) {
            return { ...state, medicationList: action.payload };
        },
        setPrescriberList(state, action) {
            return { ...state, prescriberList: action.payload };
        },
        setSupplierList(state, action) {
            return { ...state, supplierList: action.payload };
        },
        setSupplierDetails(state, action) {
            const { supplierNpi, supplier } = action.payload;
            return {
                ...state,
                supplierDetails: {
                    ...state.supplierDetails,
                    [supplierNpi]: supplier,
                },
            };
        },
        setProductConfigList(state, action) {
            const { payerId, productServiceOid, productConfig } = action.payload;
            return {
                ...state,
                productConfigList: {
                    ...state.productConfigList,
                    [payerId]: {
                        ...state.productConfigList[payerId],
                        [productServiceOid]: productConfig,
                    },
                },
            };
        },
        setPlaceOfServiceList(state, action) {
            return { ...state, placeOfServiceList: action.payload };
        },
    },
});

export const {
    setDiagnosisList,
    setPayerList,
    setMedicationList,
    setPrescriberList,
    setSupplierList,
    setSupplierDetails,
    setProductConfigList,
    setPlaceOfServiceList,
} = referenceSlice.actions;

export const fetchDiagnoses = () => async (dispatch) => {
    try {
        const diagnoses = await getDiagnoses();
        const diagnosisList = diagnoses
            .reduce(
                (accumulator, diagnosis) => [
                    ...accumulator,
                    ...(diagnosis.codes?.map((code) => ({
                        description: code.description,
                        code: code.code,
                        oid: diagnosis.oid,
                        // Select properties
                        name: code.description,
                        secondaryData: code.code,
                    })) ?? []),
                ],
                [],
            )
            .sort((a, b) => sortAsc(a.description, b.description));
        dispatch(setDiagnosisList(diagnosisList));
    } catch (err) {
        handleError('An error occurred retrieving diagnoses.', err);
    }
};

export const fetchMedications = (segmentId) => async (dispatch) => {
    try {
        const medications = await getMedications(segmentId);
        const medicationsMap = {};
        medications.forEach((medication) => {
            medicationsMap[medication.product_service_oid] = {
                description: medication.description,
            };
        });
        dispatch(setMedicationList(medicationsMap));
    } catch (err) {
        handleError('An error occurred retrieving medications.', err);
    }
};

export const fetchPayers = () => async (dispatch) => {
    try {
        const payers = await getPayers();
        const payerList = payers
            .map((payer) => ({
                id: payer.payer_id,
                name: payer.payer_name,
            }))
            .sort((a, b) => sortAsc(a.name, b.name));
        dispatch(setPayerList(payerList));
    } catch (err) {
        handleError('An error occurred retrieving health plans.', err);
    }
};

export const fetchPrescriberInfo = () => async (dispatch, getState) => {
    try {
        const { selectedNpi } = getState().auth;
        const prescriberInfo = await getPrescriberInfo(selectedNpi);
        const prescriberList = prescriberInfo
            .map((prescriber) => ({
                npi: prescriber.prescriber_npi,
                name: formatNameLastFirst(prescriber.last_name, prescriber.first_name),
                phone: prescriber.telephone_number,
                practiceNpi: prescriber.practice_npi,
            }))
            .sort((a, b) => sortAsc(a.name, b.name));
        dispatch(setPrescriberList(prescriberList));
    } catch (err) {
        handleError('An error occurred retrieving prescriber information.', err);
    }
};

export const fetchSuppliers = () => async (dispatch) => {
    try {
        const response = await getParentSuppliers();
        const supplierList = response.map((supplier) => ({
            npi: supplier.supplier_npi,
            name: supplier.organization_name,
            networkStatus: supplier.network_status,
        }));
        dispatch(setSupplierList(supplierList));
    } catch (err) {
        handleError('An error occurred retrieving supplier information.', err);
    }
};

export const fetchSupplierDetails = (supplierNpi) => async (dispatch) => {
    try {
        const response = await getSupplierInfo(supplierNpi);
        const supplier = {
            name: response.organization_name,
            phone: response.telephone_number,
            fax: response.fax_number,
            email: response.email,
            address1: response.address_1,
            address2: response.address_2,
            city: response.city,
            state: response.state,
            zip: response.postal_code,
            npi: response.supplier_npi,
        };
        dispatch(setSupplierDetails({ supplierNpi, supplier }));
    } catch (err) {
        handleError('An error occurred retrieving supplier details.', err);
    }
};

export const fetchPlacesOfService = () => async (dispatch) => {
    try {
        const response = await getPlacesOfService();
        const placesOfService = response
            .map((option) => ({
                code: option.code,
                name: option.description,
            }))
            .sort((a, b) => sortAsc(a.name, b.name));
        dispatch(setPlaceOfServiceList(placesOfService));
    } catch (err) {
        handleError('An error occurred retrieving place of service options.', err);
    }
};
